import PropTypes from 'prop-types';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography } from '@material-ui/core';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import useAuth from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';

/**
 * Protège une page par une permission du catalogue.
 *
 * `fallback` = l'ancienne règle en dur (booléen, ou fonction de l'utilisateur),
 * utilisée seulement tant que l'API ne renvoie pas de permissions (voir
 * usePermissions). Dans ce cas un refus redirige vers le tableau de bord,
 * comme le faisait TaskTrackingGuard.
 */
const PermissionGuard = ({ perm, fallback, children }) => {
  const { user } = useAuth();
  const { ready, can } = usePermissions();
  const legacy = typeof fallback === 'function' ? Boolean(user && fallback(user)) : fallback;

  if (can(perm, legacy)) {
    return <>{children}</>;
  }

  if (!ready) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 2,
        px: 2
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'action.hover',
          color: 'text.secondary'
        }}
      >
        <LockRoundedIcon sx={{ fontSize: 30 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: 'text.primary' }}>
        Accès non autorisé
      </Typography>
      <Typography sx={{ color: 'text.secondary', maxWidth: 420 }}>
        Votre rôle ne vous donne pas accès à cette page. Si vous en avez besoin,
        demandez à un administrateur de l&apos;ajouter à votre rôle.
      </Typography>
      <Button component={RouterLink} to="/dashboard" variant="outlined" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}>
        Retour au tableau de bord
      </Button>
    </Box>
  );
};

PermissionGuard.propTypes = {
  perm: PropTypes.string.isRequired,
  fallback: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  children: PropTypes.node
};

PermissionGuard.defaultProps = {
  fallback: true
};

export default PermissionGuard;
